import React from 'react';
import { Menu } from 'antd';
import { CrewTimelineItem } from '../CrewTimeline/generator';
import Guard from '../../../../../../permissions/crew-schedule-v2-guard';
import { MenuItemType } from '../../../types/MenuItemTypes';
import { CrewTimelineEntryType } from '../../../../../../../common/types/timeline/CrewTimelineEntryType';
import { FeatureFlag } from '../../../../../../../utils/feature-flags/FeatureFlagsProvider';
import { AircraftTimelineItem } from '../AircraftTimeline/generator';
import { TimelineType } from '../../../types/timeline';
import { AircraftTimelineEntryType } from '../../../../../../../common/types/timeline/AircraftTimelineEntryType';
import { IDs } from '../../../../../../../constants';
import { ContractTimelineItem } from '../ContractTimeline/generator';
import { ContractTimelineEntryType } from '../../../../../../../common/types/timeline/ContractTimelineEntryType';
import { CompanySettings } from '../../../../../../../common/types/Company';

import {
  isHideCSEntryDetails,
  isHideCSEntryDetailsForUser,
  isHideMaintenanceEntryDetails,
  isHideMaintenanceEntryDetailsForUser,
} from '../../../helpers';
import { MaintenanceTimelineItem } from '../MaintenanceTimeline/generator';
import { MaintenanceTimelineEntryType } from '../../../../../../../common/types/timeline/MaintenanceTimelineEntryType';

class MenuItemBuilder {
  private items: React.ReactNode[];
  private item:
    | CrewTimelineItem
    | AircraftTimelineItem
    | ContractTimelineItem
    | MaintenanceTimelineItem;
  private userRoles: string[];
  private featureFlags: { [flag: string]: boolean };
  private guard: Guard;

  constructor(
    item: CrewTimelineItem | AircraftTimelineItem | ContractTimelineItem | MaintenanceTimelineItem,
    userRoles: string[],
    featureFlags: { [flag: string]: boolean },
    guard: Guard,
  ) {
    this.items = [];
    this.item = item;
    this.userRoles = userRoles;
    this.featureFlags = featureFlags;
    this.guard = guard;
  }

  addItem(type: MenuItemType, label: string, danger = false, id = null) {
    this.items.push(
      <Menu.Item key={type} id={id} danger={danger}>
        {label}
      </Menu.Item>,
    );
    return this;
  }

  build(): React.ReactNode[] {
    return this.items;
  }
}

export const canDeleteItem = (
  entryType:
    | CrewTimelineEntryType
    | AircraftTimelineEntryType
    | ContractTimelineEntryType
    | MaintenanceTimelineEntryType,
  guard: Guard,
): boolean => {
  switch (entryType) {
    case CrewTimelineEntryType.CrewSchedule:
      return guard.canAccess('actions.deleteScheduleEntry');
    case CrewTimelineEntryType.DutySchedule:
      return guard.canAccess('actions.deleteDutyEntry');
    case CrewTimelineEntryType.DutyTimes:
      return guard.canAccess('actions.deleteDutyTime');
    case CrewTimelineEntryType.PilotContract:
      return guard.canAccess('actions.deletePilotContractEntry');
    case AircraftTimelineEntryType.AircraftSchedule:
      return guard.canAccess('actions.deleteScheduleEntry');
    case AircraftTimelineEntryType.MaintenanceAircraftSchedule:
      return guard.canAccess('actions.deleteMaintenanceEntry');
    case MaintenanceTimelineEntryType.MaintenanceCrew:
      return guard.canAccess('actions.deleteMaintenanceEntry');
    default:
      return false;
  }
};

export const getItemId = (
  itemType: MenuItemType,
  entryType:
    | CrewTimelineEntryType
    | AircraftTimelineEntryType
    | ContractTimelineEntryType
    | MaintenanceTimelineEntryType,
): string => {
  if (itemType === MenuItemType.Delete) {
    switch (entryType) {
      case CrewTimelineEntryType.CrewSchedule:
        return IDs.timeline.contextDeleteCrewScheduleEntry;
      case CrewTimelineEntryType.DutySchedule:
        return IDs.timeline.contextDeleteDutyScheduleEntry;
      case CrewTimelineEntryType.MeasuringTape:
        return IDs.timeline.contextDeleteMeasuringTape;
      case CrewTimelineEntryType.ExtensionPeriod:
        return IDs.timeline.contextDeleteExtensionPeriod
      case AircraftTimelineEntryType.AircraftSchedule:
        return IDs.timeline.contextDeleteBulkScheduleEntry;
      case AircraftTimelineEntryType.MaintenanceAircraftSchedule:
        return IDs.timeline.contextDeleteBulkMXScheduleEntry;
      case MaintenanceTimelineEntryType.MaintenanceCrew:
        return IDs.timeline.contextDeleteMaintenanceEntry;
      case AircraftTimelineEntryType.MeasuringTape:
        return IDs.timeline.contextDeleteAircraftMeasuringTape;
      case AircraftTimelineEntryType.ScheduleTemplate:
        return IDs.timeline.contextDeleteScheduleTemplate;
    }
  }

  if (itemType === MenuItemType.Copy) {
    switch (entryType) {
      case CrewTimelineEntryType.CrewSchedule:
        return IDs.timeline.contextCopyScheduleEntry;
      case CrewTimelineEntryType.DutySchedule:
        return IDs.timeline.contextCopyDutyScheduleEntry;
      case MaintenanceTimelineEntryType.MaintenanceCrew:
        return IDs.timeline.contextCopyMaintenanceEntry;
    }
  }

  if (itemType === MenuItemType.Cut) {
    switch (entryType) {
      case CrewTimelineEntryType.CrewSchedule:
        return IDs.timeline.contextCutScheduleEntry;
      case CrewTimelineEntryType.DutySchedule:
        return IDs.timeline.contextCutDutyScheduleEntry;
      case MaintenanceTimelineEntryType.MaintenanceCrew:
        return IDs.timeline.contextCutMaintenanceEntry;
    }
  }

  if (itemType === MenuItemType.BulkDelete) {
    switch (entryType) {
      case CrewTimelineEntryType.CrewSchedule:
        return IDs.timeline.contextBulkDeleteCrewScheduleEntry;
      case MaintenanceTimelineEntryType.MaintenanceCrew:
        return IDs.timeline.contextBulkDeleteMXScheduleEntry;
      case CrewTimelineEntryType.DutySchedule:
        return IDs.timeline.contextBulkDeleteDutyScheduleEntry;
      case AircraftTimelineEntryType.AircraftSchedule:
        return IDs.timeline.contextBulkDeleteBulkScheduleEntry;
    }
  }

  if (itemType === MenuItemType.BulkCopy) {
    switch (entryType) {
      case CrewTimelineEntryType.CrewSchedule:
        return IDs.timeline.contextBulkCopyScheduleEntry;
      case MaintenanceTimelineEntryType.MaintenanceCrew:
        return IDs.timeline.contextBulkCopyMXScheduleEntry;
      case CrewTimelineEntryType.DutySchedule:
        return IDs.timeline.contextBulkCopyDutyScheduleEntry;
    }
  }

  if (itemType === MenuItemType.BulkCut) {
    switch (entryType) {
      case CrewTimelineEntryType.CrewSchedule:
        return IDs.timeline.contextBulkCutScheduleEntry;
      case MaintenanceTimelineEntryType.MaintenanceCrew:
        return IDs.timeline.contextBulkCutMXScheduleEntry;
      case CrewTimelineEntryType.DutySchedule:
        return IDs.timeline.contextBulkCutDutyScheduleEntry;
    }
  }
  return '';
};

export const canEditItem = (
  entryType:
    | CrewTimelineEntryType
    | AircraftTimelineEntryType
    | ContractTimelineEntryType
    | MaintenanceTimelineEntryType,
  guard: Guard,
): boolean => {
  switch (entryType) {
    case CrewTimelineEntryType.CrewSchedule:
      return guard.canAccess('actions.updateScheduleEntry');
    case CrewTimelineEntryType.DutySchedule:
      return guard.canAccess('actions.updateDutyEntry');
    case CrewTimelineEntryType.DutyTimes:
      return guard.canAccess('actions.updateDutyTime');
    case CrewTimelineEntryType.PilotContract:
      return guard.canAccess('actions.editPilotContract');
    case AircraftTimelineEntryType.AircraftSchedule:
      return guard.canAccess('actions.addScheduleEntry');
    case AircraftTimelineEntryType.MaintenanceAircraftSchedule:
      return guard.canAccess('actions.updateMaintenanceEntry');
    case MaintenanceTimelineEntryType.MaintenanceCrew:
      return guard.canAccess('actions.updateMaintenanceEntry');
    default:
      return false;
  }
};

export const getCrewScheduleMenuItems = (
  timelineType: TimelineType,
  item: CrewTimelineItem | AircraftTimelineItem | ContractTimelineItem | MaintenanceTimelineItem,
  userRoles: string[],
  featureFlags: { [flag: string]: boolean },
  guard: Guard,
  authUserID: string,
  companySettings: CompanySettings,
  selectedItems: (CrewTimelineItem | AircraftTimelineItem | MaintenanceTimelineItem)[],
): React.ReactNode[] => {
  if (!item) {
    return [];
  }
  const entryType = item.entryType;
  const isAdmin = userRoles.includes('admin');
  const isPilot = userRoles.includes('pilot');
  const isMaintenance = userRoles.includes('engineer');
  const canPilotCopyOwnSchedule = isPilot && companySettings?.PilotsCanCopyPasteSchedule &&
    item.entryType === CrewTimelineEntryType.DutySchedule && item.dutyEntry?.UserID === authUserID;

  const builder = new MenuItemBuilder(item, userRoles, featureFlags, guard);

  if (item.isNewEntry) {
    if (
      item.entryType !== CrewTimelineEntryType.MeasuringTape &&
      item.entryType !== AircraftTimelineEntryType.MeasuringTape
    ) {
      builder.addItem(MenuItemType.Edit, 'Edit');
    }
    return builder
      .addItem(MenuItemType.Delete, 'Delete', true, getItemId(MenuItemType.Delete, entryType))
      .build();
  }

  if (item.entryType === CrewTimelineEntryType.DutySchedule && item.dutyEntry.IsAwaitingForUpdate) {
    return [];
  }
  
  if (canPilotCopyOwnSchedule) {
      if (
        featureFlags[FeatureFlag.CrewScheduleCopyPaste] &&
        !selectedItems.map(it => it.id).includes(item.id)
      ) {
        builder.addItem(MenuItemType.Copy, 'Copy', false, getItemId(MenuItemType.Copy, entryType));
        builder.addItem(MenuItemType.Cut, 'Cut', false, getItemId(MenuItemType.Cut, entryType));
      } else if (
        featureFlags[FeatureFlag.CrewScheduleCopyPaste] &&
        selectedItems.map(it => it.id).includes(item.id) &&
        selectedItems.map(it => it.group).every(group => group === item.group)
      ) {
        builder.addItem(
          MenuItemType.BulkCopy,
          'Copy All',
          false,
          getItemId(MenuItemType.BulkCopy, entryType),
        );
        builder.addItem(
          MenuItemType.BulkCut,
          'Cut All',
          false,
          getItemId(MenuItemType.BulkCut, entryType),
        );
      }
  }
  if (isAdmin) {
    if (
      timelineType === TimelineType.Aircraft &&
      entryType === AircraftTimelineEntryType.AircraftSchedule
    ) {
      builder.addItem(MenuItemType.ShowAssociatedCrew, 'Show Associated Crew');
      if (item.aircraftEntry.CrewScheduleGroupID) {
        builder.addItem(MenuItemType.ShowAssociatedGroupEntries, 'Show Associated Group Entries');
      }
    }

    if (
      timelineType === TimelineType.Aircraft &&
      entryType === AircraftTimelineEntryType.MaintenanceAircraftSchedule
    ) {
      builder.addItem(MenuItemType.ShowAssociatedMaintenance, 'Show Associated Maintenance');
    }

    if (
      timelineType === TimelineType.Aircraft &&
      entryType === AircraftTimelineEntryType.ScheduleTemplate
    ) {
      builder.addItem(
        MenuItemType.Delete,
        'Delete',
        true,
        getItemId(MenuItemType.Delete, entryType),
      );
    }

    if (
      timelineType === TimelineType.Crew &&
      entryType === CrewTimelineEntryType.ExtensionPeriod
    ) {
      builder.addItem(
        MenuItemType.Delete,
        'Delete',
        true,
        getItemId(MenuItemType.Delete, entryType),
      );
    }

    if (timelineType === TimelineType.Contract) {
      if (entryType === ContractTimelineEntryType.AircraftContract) {
        builder.addItem(
          MenuItemType.ShowAssociatedAircraft,
          `Show Associated ${companySettings?.CompanyFieldNameMappings?.CrewScheduleAircraft ||
            'Aircraft'}`,
        );
        builder.addItem(MenuItemType.UnassignContract, 'Unassign Contract');
        builder.addItem(
          MenuItemType.Delete,
          `Delete ${companySettings?.CompanyFieldNameMappings?.CrewScheduleAircraft ||
            'Aircraft'} Contract`,
          true,
          getItemId(MenuItemType.Delete, entryType),
        );
      }
      if (entryType === ContractTimelineEntryType.PilotSchedule) {
        builder.addItem(MenuItemType.ShowAssociatedCrew, 'Show Associated Crew');
        builder.addItem(MenuItemType.UnassignContract, 'Unassign Contract');
        builder.addItem(MenuItemType.UnassignAircraft, 'Unassign Aircraft');
      }
    }

    if (canEditItem(entryType, guard)) {
      if (
        entryType === CrewTimelineEntryType.CrewSchedule &&
        featureFlags[FeatureFlag.BulkScheduleGenerator]
      ) {
        // TODO: recover this feature
        // builder.addItem(MenuItemType.GenerateSchedule, 'Generate Schedule');
      }

      builder.addItem(MenuItemType.Edit, 'Edit');

      const copyPasteEntryTypes: (
        | AircraftTimelineEntryType
        | ContractTimelineEntryType
        | CrewTimelineEntryType
        | MaintenanceTimelineEntryType
      )[] = [
        CrewTimelineEntryType.CrewSchedule,
        CrewTimelineEntryType.DutySchedule,
        AircraftTimelineEntryType.AircraftSchedule,
        AircraftTimelineEntryType.MaintenanceAircraftSchedule,
        MaintenanceTimelineEntryType.MaintenanceCrew,
      ];

      if (
        copyPasteEntryTypes.includes(entryType) &&
        featureFlags[FeatureFlag.CrewScheduleCopyPaste] &&
        !selectedItems.map(it => it.id).includes(item.id)
      ) {
        builder.addItem(MenuItemType.Copy, 'Copy', false, getItemId(MenuItemType.Copy, entryType));
        builder.addItem(MenuItemType.Cut, 'Cut', false, getItemId(MenuItemType.Cut, entryType));
      } else if (
        copyPasteEntryTypes.includes(entryType) &&
        featureFlags[FeatureFlag.CrewScheduleCopyPaste] &&
        selectedItems.map(it => it.id).includes(item.id) &&
        selectedItems.map(it => it.group).every(group => group === item.group)
      ) {
        builder.addItem(
          MenuItemType.BulkCopy,
          'Copy All',
          false,
          getItemId(MenuItemType.BulkCopy, entryType),
        );
        builder.addItem(
          MenuItemType.BulkCut,
          'Cut All',
          false,
          getItemId(MenuItemType.BulkCut, entryType),
        );
      }
    }

    if (canDeleteItem(entryType, guard) && !selectedItems.map(it => it.id).includes(item.id)) {
      builder.addItem(
        MenuItemType.Delete,
        'Delete',
        true,
        getItemId(MenuItemType.Delete, entryType),
      );
      if (
        item.entryType === AircraftTimelineEntryType.AircraftSchedule &&
        item.aircraftEntry.CrewScheduleGroupID
      ) {
        builder.addItem(MenuItemType.RemoveFromGroup, 'Remove From Group', true);
      }
    } else if (
      canDeleteItem(entryType, guard) &&
      selectedItems.map(it => it.id).includes(item.id)
    ) {
      builder.addItem(
        MenuItemType.BulkDelete,
        'Delete All',
        true,
        getItemId(MenuItemType.BulkDelete, entryType),
      );
    }
  } else if (isPilot) {
    if (entryType === CrewTimelineEntryType.CrewSchedule) {
      const hideCSEntryDetailsForUser = isHideCSEntryDetailsForUser(
        featureFlags,
        userRoles,
        authUserID,
        (item as CrewTimelineItem)?.crewEntry?.UserID,
      );
      if (!hideCSEntryDetailsForUser) {
        builder.addItem(MenuItemType.View, 'View');
      }
    } else if (entryType === CrewTimelineEntryType.AircraftSchedule) {
      const hideCSEntryDetails = isHideCSEntryDetails(featureFlags, userRoles);
      if (!hideCSEntryDetails) {
        builder.addItem(MenuItemType.View, 'View');
      }
      const hideMXEntryDetails = isHideMaintenanceEntryDetails(featureFlags, userRoles);
      if (!hideMXEntryDetails) {
        builder.addItem(MenuItemType.View, 'View');
      }
    } else {
      builder.addItem(MenuItemType.View, 'View');
    }

    if (entryType === CrewTimelineEntryType.DutyTimes) {
      builder.addItem(MenuItemType.Edit, 'Edit');
      builder.addItem(
        MenuItemType.Delete,
        'Delete',
        true,
        getItemId(MenuItemType.Delete, entryType),
      );
    }
  } else if (isMaintenance) {
    if (entryType === MaintenanceTimelineEntryType.MaintenanceCrew) {
      const hideMaintenanceEntryDetailsForUser = isHideMaintenanceEntryDetailsForUser(
        featureFlags,
        userRoles,
        authUserID,
        (item as MaintenanceTimelineItem)?.maintenanceEntry?.UserID,
      );
      if (!hideMaintenanceEntryDetailsForUser) {
        builder.addItem(MenuItemType.View, 'View');
      }
    } else {
      builder.addItem(MenuItemType.View, 'View');
    }
  }

  if (companySettings.SalesModule && timelineType === 'contract') {
    builder.addItem(
      MenuItemType.Copy,
      'Copy',
      false,
      getItemId(MenuItemType.Copy, entryType),
    );
  }

  return builder.build();
};
